import { useState, useEffect } from 'react';
import { checkSubscriptionStatus, getTrialDaysRemaining, formatSubscriptionStatus, getSubscriptionColor, type SubscriptionInfo } from '../utils/subscription';

interface SubscriptionManagerProps {
  userId: string;
  onSubscriptionUpdate?: (subscription: SubscriptionInfo) => void;
}

export default function SubscriptionManager({ userId, onSubscriptionUpdate }: SubscriptionManagerProps) {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubscriptionStatus();
  }, [userId]);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const subscriptionInfo = await checkSubscriptionStatus(userId);
      setSubscription(subscriptionInfo);
      if (subscriptionInfo) {
        onSubscriptionUpdate?.(subscriptionInfo);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setError('Error al cargar la información de suscripción');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToPremium = async () => {
    try {
      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/checkout-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: userId,
          customer_email: '', // Will be filled from user data
          plan: 'premium'
        }),
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se pudo crear la sesión de pago');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      setError('Error al procesar la actualización');
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.subscriptionId || !confirm('¿Estás seguro de que quieres cancelar tu suscripción?')) {
      return;
    }

    try {
      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: userId,
          subscriptionId: subscription.subscriptionId
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('Suscripción cancelada exitosamente');
        loadSubscriptionStatus(); // Reload status
      } else {
        throw new Error(data.error || 'Error al cancelar la suscripción');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Error al cancelar la suscripción');
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="text-red-600 mb-4">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
        <button
          onClick={loadSubscriptionStatus}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="text-gray-600">
          No se pudo cargar la información de suscripción
        </div>
      </div>
    );
  }

  const trialDaysRemaining = getTrialDaysRemaining(subscription.trialEndDate);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800">
          <i className="ri-vip-crown-line mr-2 text-orange-500"></i>
          Estado de Suscripción
        </h3>
        <button
          onClick={loadSubscriptionStatus}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <i className="ri-refresh-line text-lg"></i>
        </button>
      </div>

      {/* Current Plan Status */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Plan Actual</span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSubscriptionColor(subscription.planStatus)}`}>
            {formatSubscriptionStatus(subscription.planStatus)}
          </span>
        </div>
        
        <div className="text-2xl font-bold text-gray-800 mb-1">
          {subscription.plan === 'premium' ? 'Plan Premium' : 'Plan Prueba'}
        </div>
        
        {subscription.plan === 'trial' && trialDaysRemaining > 0 && (
          <div className="text-sm text-blue-600">
            <i className="ri-time-line mr-1"></i>
            {trialDaysRemaining} días restantes de prueba
          </div>
        )}
      </div>

      {/* Subscription Details */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Estado de Suscripción:</span>
          <span className={`px-2 py-1 rounded text-xs font-medium ${getSubscriptionColor(subscription.subscriptionStatus)}`}>
            {formatSubscriptionStatus(subscription.subscriptionStatus)}
          </span>
        </div>
        
        {subscription.trialEndDate && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Fin de Prueba:</span>
            <span className="text-gray-800">
              {new Date(subscription.trialEndDate).toLocaleDateString()}
            </span>
          </div>
        )}
        
        {subscription.subscriptionEndDate && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Fin de Suscripción:</span>
            <span className="text-gray-800">
              {new Date(subscription.subscriptionEndDate).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        {!subscription.isActive && subscription.plan === 'trial' && (
          <button
            onClick={handleUpgradeToPremium}
            className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 transition-colors font-medium"
          >
            <i className="ri-vip-crown-line mr-2"></i>
            Actualizar a Premium
          </button>
        )}
        
        {subscription.isSubscriptionActive && subscription.subscriptionId && (
          <button
            onClick={handleCancelSubscription}
            className="w-full bg-red-500 text-white py-3 px-4 rounded-lg hover:bg-red-600 transition-colors font-medium"
          >
            <i className="ri-close-circle-line mr-2"></i>
            Cancelar Suscripción
          </button>
        )}
        
        {subscription.isActive && (
          <div className="text-center text-green-600 text-sm">
            <i className="ri-check-circle-line mr-1"></i>
            Tu suscripción está activa
          </div>
        )}
      </div>

      {/* Features Info */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Características del Plan</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          {subscription.plan === 'premium' ? (
            <>
              <li>• Campañas de donación ilimitadas</li>
              <li>• Casos de adopción ilimitados</li>
              <li>• Panel de administración completo</li>
              <li>• Integración con redes sociales</li>
              <li>• Herramientas de marketing</li>
            </>
          ) : (
            <>
              <li>• Acceso completo durante 30 días</li>
              <li>• Todas las funciones disponibles</li>
              <li>• Sin limitaciones</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}
